import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import Loading from './components/Loading';
import FloatingFeedbackButton from './components/FloatingFeedbackButton';
import { performanceMonitor } from './lib/performance';

// Eager load critical pages (auth and dashboard)
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import LandingPage from './pages/LandingPage';
import AuthCallbackPage from './pages/AuthCallbackPage';

// Lazy load non-critical pages
const TeamListPage = lazy(() => import('./pages/TeamListPage'));
const TeamCreatePage = lazy(() => import('./pages/TeamCreatePage'));
const TeamDetailPage = lazy(() => import('./pages/TeamDetailPage'));
const TeamSettingsPage = lazy(() => import('./pages/TeamSettingsPage'));
const RetrospectiveCreatePage = lazy(() => import('./pages/RetrospectiveCreatePage'));
const RetrospectiveSessionPage = lazy(() => import('./pages/RetrospectiveSessionPage'));
const RetrospectivesListPage = lazy(() => import('./pages/RetrospectivesListPage'));
const ActionItemPage = lazy(() => import('./pages/ActionItemPage'));
const ActionItemDetailPage = lazy(() => import('./pages/ActionItemDetailPage'));
const AnalyticsDashboardPage = lazy(() => import('./pages/AnalyticsDashboardPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'));

const UserSearchPage = lazy(() => import('./pages/UserSearchPage'));
const InviteJoinPage = lazy(() => import('./pages/InviteJoinPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const EmailVerificationPendingPage = lazy(() => import('./pages/EmailVerificationPendingPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const BillingSettingsPage = lazy(() => import('./pages/BillingSettingsPage'));

// Admin pages (lazy loaded)
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminTeams = lazy(() => import('./pages/admin/AdminTeams'));
const AdminInvites = lazy(() => import('./pages/admin/AdminInvites'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminAuditLogs = lazy(() => import('./pages/admin/AdminAuditLogs'));
const AdminEarlyAccess = lazy(() => import('./pages/admin/AdminEarlyAccess'));
const AdminFeedback = lazy(() => import('./pages/admin/AdminFeedback'));
const EarlyAccessRequestPage = lazy(() => import('./pages/EarlyAccessRequestPage'));

function App() {
  useEffect(() => {
    // Mark app initialization
    performanceMonitor.mark('app-init');

    // Report metrics after initial render
    const timer = setTimeout(() => {
      performanceMonitor.mark('app-ready');
      performanceMonitor.measure('app-initialization', 'app-init', 'app-ready');
    }, 0);

    return () => clearTimeout(timer);
  }, []);

  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <NotificationProvider>            
            <Suspense fallback={<Loading />}>
              <Routes>
                {/* Public routes */}
                {/* Public routes */}
                <Route path="/invite/:token" element={<InviteJoinPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/verify-email-pending" element={<EmailVerificationPendingPage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsOfServicePage />} />
                <Route path="/early-access" element={<EarlyAccessRequestPage />} />
                <Route path="/" element={<LandingPage />} />

                {/* Protected routes */}

                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teams"
                  element={
                    <ProtectedRoute>
                      <TeamListPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teams/create"
                  element={
                    <ProtectedRoute>
                      <TeamCreatePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teams/:id"
                  element={
                    <ProtectedRoute>
                      <TeamDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teams/:id/settings"
                  element={
                    <ProtectedRoute>
                      <TeamSettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/teams/:teamId/retrospectives/create"
                  element={
                    <ProtectedRoute>
                      <RetrospectiveCreatePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/retrospectives/create"
                  element={
                    <ProtectedRoute>
                      <RetrospectiveCreatePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/retrospectives"
                  element={
                    <ProtectedRoute>
                      <RetrospectivesListPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/retrospectives/:sessionId"
                  element={
                    <ProtectedRoute>
                      <RetrospectiveSessionPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/action-items"
                  element={
                    <ProtectedRoute>
                      <ActionItemPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/action-items/:id"
                  element={
                    <ProtectedRoute>
                      <ActionItemDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute>
                      <AnalyticsDashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/billing"
                  element={
                    <ProtectedRoute>
                      <BillingSettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/users/search"
                  element={
                    <ProtectedRoute>
                      <UserSearchPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/users/:userId"
                  element={
                    <ProtectedRoute>
                      <UserProfilePage />
                    </ProtectedRoute>
                  }
                />

                {/* Admin routes */}
                <Route
                  path="/admin"
                  element={
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <AdminRoute>
                      <AdminUsers />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/teams"
                  element={
                    <AdminRoute>
                      <AdminTeams />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/invites"
                  element={
                    <AdminRoute>
                      <AdminInvites />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/settings"
                  element={
                    <AdminRoute>
                      <AdminSettings />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/audit-logs"
                  element={
                    <AdminRoute>
                      <AdminAuditLogs />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/early-access"
                  element={
                    <AdminRoute>
                      <AdminEarlyAccess />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/feedback"
                  element={
                    <AdminRoute>
                      <AdminFeedback />
                    </AdminRoute>
                  }
                />

                {/* Catch-all route */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
            <FloatingFeedbackButton />
          </NotificationProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
